import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import TextInput from '../../Components/TextInput';
import Label from '../../Components/Label';
import SnippetService from '../../Services/SnippetService';
import Button from '../../Components/Button';
import SuccessAlert from '../../Components/SuccessAlert';
import ClickCopyLabel from '../../Components/ClickCopyLabel';
import StringUtils from '../../Utils/StringUtils';
import { NotificationManager } from 'react-notifications';
import Header from '../../Components/Heading';

const snippetType = 'ImageSnippet';

export default function ImageSnippet() {
	const [enable, setEnable] = useState(true);
	const [res, setRes] = useState(null);

	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (values, { resetForm }) => {
			processSubmit(setEnable, values, setRes, resetForm);
		}
	});

	useEffect(
		() => {
			if (res && res.success) {
				NotificationManager.success(`Created ${res.snippetId}`);
				formik.setValues({ ...initialValues });
				formik.resetForm({ ...initialValues });
			} else if (res && !res.success) {
				NotificationManager.error(res.message);
			}
		},
		[res]
	);

	/* Dropzone */
	const onDrop = useCallback((acceptedFiles) => {
		acceptedFiles.forEach((file) => {
			console.log(file.type);
			formik.setFieldValue('image', file);
		});
	}, []);
	const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
		accept: {
			'image/*': ['.jpeg', '.png', '.jpg', '.tiff', '.gif', '.webp']
		},
		onDrop,
		disabled: !enable,
		multiple: false
	});
	const acceptedFileItems = acceptedFiles.map((file) => (
		<li key={file.path}>
			{file.path} - {file.size} bytes
		</li>
	));
	const fileRejectionItems = fileRejections.map(({ file, errors }) => (
		<li key={file.path}>
			{file.path} - {file.size} bytes
			<ul>{errors.map((e) => <li key={e.code}>{e.message}</li>)}</ul>
		</li>
	));

	return (
		<div className="grid justify-items-center">
			<Header text="Create Image Snippet" type="h2" />
			{res &&
				res.success && (
					<SuccessAlert>
						<div className="flex items-center">
							<ClickCopyLabel text={res.snippetId} label="Snippet Id" />
							<ClickCopyLabel text={res.image} label="Image Url" />
						</div>
					</SuccessAlert>
				)}

			<form className="w-4/5">
				<Label text="Image" />
				<section>
					<div
						{...getRootProps()}
						style={{
							border: '2px solid blue',
							height: '300px',
							position: 'relative',
							cursor: 'pointer'
						}}
					>
						<input {...getInputProps()} />
						<div
							style={{
								margin: 'auto',
								position: 'absolute',
								top: '50%',
								transform: 'translate(-50%, -50%)',
								left: '50%'
							}}
						>
							<p>Drag and drop one image here, or click to select files</p>
							<h4>Accepted file</h4>
							<ul>{acceptedFileItems}</ul>
							<h4>Rejected files</h4>
							<ul>{fileRejectionItems}</ul>
							{formik.errors.image && <div className="text-red-600">{formik.errors.image}</div>}
						</div>
					</div>
				</section>

				<TextInput
					label="Caption"
					placeholder="Caption"
					name="caption"
					onChange={formik.handleChange}
					value={formik.values.caption}
					disabled={!enable}
					error={formik.errors.caption}
				/>
				<TextInput
					label="Width"
					placeholder="Width"
					name="width"
					onChange={formik.handleChange}
					value={formik.values.width}
					disabled={!enable}
					error={formik.errors.width}
				/>
				<TextInput
					label="Height"
					placeholder="Height"
					name="height"
					onChange={formik.handleChange}
					value={formik.values.height}
					disabled={!enable}
					error={formik.errors.height}
				/>
				<TextInput
					label="Comments"
					placeholder="Comments"
					name="comments"
					onChange={formik.handleChange}
					value={formik.values.comments}
					disabled={!enable}
					error={formik.errors.comments}
				/>
				<Button text="Submit" disabled={!enable} type="submit" onClick={formik.handleSubmit} />
			</form>
		</div>
	);
}

function processSubmit(setEnable, values, setRes, resetForm) {
	setEnable(false);
	const { image, caption, comments } = values;
	const imageName = `${caption.replace(/\W/g, '')}-${Date.now()}`;
	SnippetService.createImageSnippet(image, imageName, {
		snippetType,
		alt: caption,
		caption,
		comments
	})
		.then((res) => {
			console.log('res => ', res);
			setRes({
				snippetId: StringUtils.wrapSnippetId(res.snippetId),
				image: res.imageUrl,
				success: true
			});
			resetForm();
			setEnable(true);
		})
		.catch((e) => {
			console.error('ERROR');
			console.error(e);
		});
}

const validationSchema = Yup.object().shape({
	image: Yup.mixed().required('File is required'),
	caption: Yup.string()
		.min(3, 'Too Short! It should be more than 3')
		.max(61, 'Too Long! It should be less than 160')
		.required('Required'),
	width: Yup.number().nullable(),
	height: Yup.number().nullable(),
	comments: Yup.string().nullable()
});

const initialValues = {
	image: null,
	caption: null,
	comments: null,
	width: null,
	height: null
};
